import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import {
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
// Removed Nivo Sankey - using Recharts only
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  FunnelChart,
  Funnel,
  LabelList,
} from 'recharts';

const colors = {
  primary: '#0078D4',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  chart: ['#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438'],
};

const crossClassMetrics = Array.from({ length: 12 }, (_, i) => ({
  class: `Class ${i + 1}`,
  attendance: Math.floor(85 + Math.random() * 12),
  engagement: Math.floor(70 + Math.random() * 20),
  quizAvg: Math.floor(75 + Math.random() * 15),
  nps: Math.floor(45 + Math.random() * 15),
}));

const instructorComparison = [
  { instructor: 'Prof. García', classes: 8, avgNPS: 58, avgEngagement: 82, avgQuiz: 85 },
  { instructor: 'Prof. Martínez', classes: 6, avgNPS: 52, avgEngagement: 78, avgQuiz: 81 },
  { instructor: 'Prof. López', classes: 10, avgNPS: 55, avgNPS: 80, avgQuiz: 83 },
  { instructor: 'Prof. Rodríguez', classes: 7, avgNPS: 49, avgEngagement: 75, avgQuiz: 79 },
];

const contentEffectiveness = [
  { content: 'Fundamentals', understanding: 88, retention: 85, application: 78 },
  { content: 'Case Studies', understanding: 92, retention: 90, application: 88 },
  { content: 'Tools Demo', understanding: 75, retention: 72, application: 82 },
  { content: 'Theory', understanding: 70, retention: 68, application: 65 },
  { content: 'Group Work', understanding: 85, retention: 88, application: 92 },
];

const studentJourneySankey = {
  nodes: [
    { id: 'Enrolled', color: 'hsl(210, 70%, 50%)' },
    { id: 'Active Attendance', color: 'hsl(180, 70%, 50%)' },
    { id: 'High Engagement', color: 'hsl(150, 70%, 50%)' },
    { id: 'Quiz Pass', color: 'hsl(120, 70%, 50%)' },
    { id: 'Completed', color: 'hsl(90, 70%, 50%)' },
    { id: 'Dropped', color: 'hsl(0, 70%, 50%)' },
  ],
  links: [
    { source: 'Enrolled', target: 'Active Attendance', value: 180 },
    { source: 'Enrolled', target: 'Dropped', value: 20 },
    { source: 'Active Attendance', target: 'High Engagement', value: 150 },
    { source: 'Active Attendance', target: 'Dropped', value: 30 },
    { source: 'High Engagement', target: 'Quiz Pass', value: 135 },
    { source: 'High Engagement', target: 'Dropped', value: 15 },
    { source: 'Quiz Pass', target: 'Completed', value: 128 },
    { source: 'Quiz Pass', target: 'Dropped', value: 7 },
  ]
};

const retentionFunnel = [
  { stage: 'Enrolled', value: 200, fill: colors.chart[0] },
  { stage: 'Started', value: 188, fill: colors.chart[1] },
  { stage: 'Active (>50%)', value: 165, fill: colors.chart[2] },
  { stage: 'Engaged (>75%)', value: 142, fill: colors.chart[3] },
  { stage: 'Completed', value: 128, fill: colors.chart[4] },
];

const AcademicOverview = () => {
  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          🎓 Academic Overview - Aggregate Insights
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Chip label="All Courses" color="primary" size="small" />
          <Chip label="Last 12 Sessions" variant="outlined" size="small" />
          <Chip label="4 Instructors" variant="outlined" size="small" />
        </Box>
      </Box>

      {/* Cross-Class Trends */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Cross-Class Performance Trends
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={crossClassMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="class" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="attendance" fill={alpha(colors.primary, 0.7)} name="Attendance %" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="engagement" stroke={colors.success} strokeWidth={3} dot={{ r: 4 }} name="Engagement %" />
                <Line yAxisId="right" type="monotone" dataKey="nps" stroke={colors.warning} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} name="NPS" />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Student Journey - Replaced Sankey with stacked area showing progression */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Student Journey Progression
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Cohort progression through the learning journey
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={[
                { stage: 'Enrolled', students: 200 },
                { stage: 'Started', students: 188 },
                { stage: 'Active', students: 180 },
                { stage: 'Engaged', students: 150 },
                { stage: 'Passed Quiz', students: 135 },
                { stage: 'Completed', students: 128 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 200]} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="students" stroke={colors.primary} fill={alpha(colors.primary, 0.6)} name="Active Students" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Funnel & Content */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Retention Funnel (Smooth Curves)
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
              Student retention through program lifecycle
            </Typography>
            <svg width={500} height={400} viewBox="0 0 500 400">
              <defs>
                <linearGradient id="funnel-grad-1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={colors.chart[0]} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={colors.chart[0]} stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="funnel-grad-2" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={colors.chart[1]} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={colors.chart[1]} stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="funnel-grad-3" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={colors.chart[2]} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={colors.chart[2]} stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="funnel-grad-4" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={colors.chart[3]} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={colors.chart[3]} stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="funnel-grad-5" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={colors.chart[4]} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={colors.chart[4]} stopOpacity={0.7} />
                </linearGradient>
              </defs>
              
              {retentionFunnel.map((stage, index) => {
                const total = retentionFunnel[0].value;
                const percentage = stage.value / total;
                const width = 300 * percentage;
                const x = 100 + (300 - width) / 2;
                const y = 30 + index * 70;
                const height = 60;
                
                // Curvas suaves estilo Nivo
                const topLeft = `${x},${y + 10}`;
                const topRight = `${x + width},${y + 10}`;
                const bottomLeft = `${x + 20},${y + height - 10}`;
                const bottomRight = `${x + width - 20},${y + height - 10}`;
                
                const path = `
                  M ${x},${y + 10}
                  Q ${x},${y} ${x + 10},${y}
                  L ${x + width - 10},${y}
                  Q ${x + width},${y} ${x + width},${y + 10}
                  L ${x + width - 20},${y + height - 10}
                  Q ${x + width - 20},${y + height} ${x + width - 30},${y + height}
                  L ${x + 30},${y + height}
                  Q ${x + 20},${y + height} ${x + 20},${y + height - 10}
                  Z
                `;
                
                const conversion = index < retentionFunnel.length - 1 
                  ? `${((stage.value / retentionFunnel[index].value) * 100).toFixed(1)}%`
                  : '100%';
                
                return (
                  <g key={stage.stage}>
                    <path
                      d={path}
                      fill={`url(#funnel-grad-${index + 1})`}
                      stroke="white"
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                    />
                    <text
                      x={x + width + 15}
                      y={y + height / 2}
                      fontSize={12}
                      fontWeight="600"
                      fill="#333"
                      dominantBaseline="middle"
                    >
                      {stage.stage}
                    </text>
                    <text
                      x={x + width / 2}
                      y={y + height / 2}
                      fontSize={16}
                      fontWeight="700"
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
                    >
                      {stage.value}
                    </text>
                    <text
                      x={x + width / 2}
                      y={y + height / 2 + 16}
                      fontSize={11}
                      fontWeight="600"
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      opacity={0.9}
                    >
                      {conversion}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Content Effectiveness
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={contentEffectiveness}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="content" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={80} />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="understanding" fill={colors.primary} name="Understanding" radius={[4, 4, 0, 0]} />
                <Bar dataKey="retention" fill={colors.success} name="Retention" radius={[4, 4, 0, 0]} />
                <Bar dataKey="application" fill={colors.warning} name="Application" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AcademicOverview;

